<template>
  <div class="am3-toggle-container">
    <!-- Left label if provided -->
    <label
      v-if="labelLeft"
      class="am3-toggle-label left"
      :for="toggleId"
    >{{ labelLeft }}</label>

    <!-- Toggle switch -->
    <div
      class="am3-toggle-switch"
      :class="{
        checked: modelValue,
        disabled: disabled,
        sm: size === 'sm',
      }"
      :style="color ? { '--am3-toggle-color': color } : undefined"
      :data-test-id="generateDataTestId(dataTestId, 'toggle')"
      @click="toggleValue"
    >
      <input
        :id="toggleId"
        type="checkbox"
        class="am3-toggle-input"
        :checked="modelValue"
        :disabled="disabled"
        :aria-label="ariaLabel || labelLeft || labelRight"
        @change="updateValue($event)"
      >
      <div class="am3-toggle-slider" />
    </div>

    <!-- Right label if provided -->
    <label
      v-if="labelRight"
      class="am3-toggle-label right"
      :for="toggleId"
    >{{ labelRight }}</label>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { defineComponent, ref, watch } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import '@/components/am3-generic/core/theme.css'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'

/**
 * AM3Toggle component
 *
 * A toggle/switch button with animation and optional labels
 */
export default defineComponent({
  name: 'AM3Toggle',

  props: {
    // v-model value
    modelValue: {
      type: Boolean,
      default: false,
    },

    // Left label text
    labelLeft: {
      type: String,
      default: '',
    },

    // Right label text
    labelRight: {
      type: String,
      default: '',
    },

    // Disabled state
    disabled: {
      type: Boolean,
      default: false,
    },

    // CSS color value for the checked background (overrides default brand color)
    color: {
      type: String,
      default: '',
    },

    // Size variant
    size: {
      type: String as PropType<'default' | 'sm'>,
      default: 'default',
    },

    // Custom ID for the toggle input
    id: {
      type: String,
      default: '',
    },

    // Data test id prefix
    dataTestId: {
      type: String,
      default: props => props.labelLeft || props.labelRight,
    },

    // aria labels

    ariaLabel: {
      type: String,
      default: 'Toggle',
    },

  },

  emits: ['update:modelValue', 'change'],

  setup(props, { emit }) {
    // Use provided ID or generate unique ID for the toggle
    const toggleId = ref(props.id || `am3-toggle-${uuidv4()}`)

    // Update model value
    const updateValue = (event: Event) => {
      const target = event.target as HTMLInputElement
      emit('update:modelValue', target.checked)
      emit('change', target.checked)
    }

    // Toggle the value when clicking on the switch
    const toggleValue = () => {
      if (!props.disabled) {
        emit('update:modelValue', !props.modelValue)
        emit('change', !props.modelValue)
      }
    }

    return {
      toggleId,
      updateValue,
      toggleValue,
      generateDataTestId,
    }
  },
})
</script>

<style scoped>
.am3-toggle-container {
  display: flex;
  align-items: center;
  gap: var(--padding-p-3);
}

.am3-toggle-container:focus-within .am3-toggle-slider  {
  outline: 1px solid var(--border-color-brand);

}

.am3-toggle-label {
  font-size: var(--font-size-sm);
  color: var(--font-colour-primary);
  cursor: pointer;
}

.am3-toggle-label.left {
  margin-right: var(--padding-p-1);
}

.am3-toggle-label.right {
  margin-left: var(--padding-p-1);
}

.am3-toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  cursor: pointer;
}

.am3-toggle-switch.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.am3-toggle-input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
  pointer-events: none; /* Prevents Safari from routing pointer events to the checkbox, which would double-fire toggle handlers */
}

.am3-toggle-slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--border-color-strong);
  border-radius: 24px;
  transition: 0.3s;
  outline-offset: 2px;
  outline: 1px solid transparent;
}

.am3-toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: var(--surfaces-colour-white);
  border-radius: 50%;
  transition: 0.3s;
}

.am3-toggle-switch.checked .am3-toggle-slider {
  background-color: var(--am3-toggle-color, var(--buttons-primary-default));
}

.am3-toggle-switch.checked .am3-toggle-slider:before {
  transform: translateX(20px);
}

.am3-toggle-switch:not(.disabled):hover .am3-toggle-slider:before {
  box-shadow: 0 0 2px 1px rgba(0, 0, 0, 0.1);
}

.am3-toggle-switch.checked:not(.disabled):hover .am3-toggle-slider {
  background-color: var(--am3-toggle-color, var(--buttons-primary-hover));
}

/* Small size variant */
.am3-toggle-switch.sm {
  width: 36px;
  height: 20px;
}

.am3-toggle-switch.sm .am3-toggle-slider {
  border-radius: 20px;
}

.am3-toggle-switch.sm .am3-toggle-slider:before {
  height: 14px;
  width: 14px;
  left: 3px;
  bottom: 3px;
}

.am3-toggle-switch.sm.checked .am3-toggle-slider:before {
  transform: translateX(16px);
}
</style>
